/* @moldura padrao */
/**
 * page.tsx (custos-pedido) — Lista de pedidos com custo, por loja. Tema AdminUX.
 *
 * Cards de resumo (receita/custo/margem) + tabela. Linha clica -> dashboard do
 * pedido. Botão "+ Novo pedido" -> formulário de lançamento.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import type { Loja, PedidoResumo, Resumo } from './shared';
import {
  SITUACOES,
  badgeSituacao,
  codigoDaSlug,
  corMargem,
  fmt,
  rotuloSituacao,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

const TODOS = 'todos';

/** A MARCA (ficha #44): três estados — e o vazio é "em dúvida", não "não". */
type Marca = 'acompanhar' | 'nao_acompanhar' | null;
const MARCAS: { valor: Marca; icone: string; titulo: string; ativo: string }[] = [
  { valor: 'acompanhar', icone: 'bi-check-lg', titulo: 'Acompanhar o custo', ativo: 'btn-success' },
  { valor: null, icone: 'bi-question-lg', titulo: 'Em dúvida', ativo: 'btn-secondary' },
  { valor: 'nao_acompanhar', icone: 'bi-x-lg', titulo: 'Não acompanhar', ativo: 'btn-danger' },
];

/** Situação do pedido no Tiny, como a v3 devolve (`servico_tiny_pedidos.SITUACOES`). */
const SITUACAO_TINY: Record<string, string> = {
  dados_incompletos: 'Dados incompletos', aberta: 'Aberta', aprovada: 'Aprovada',
  preparando_envio: 'Preparando envio', faturada: 'Faturada', pronto_envio: 'Pronto p/ envio',
  enviada: 'Enviada', entregue: 'Entregue', cancelada: 'Cancelada', nao_entregue: 'Não entregue',
};

interface Mudanca {
  id: number;
  tipo: 'alterado' | 'sumiu' | 'voltou';
  resumo: string | null;
  deteccao: string;
  conferencia: string | null;
  conferido_por: string | null;
}

function StatCard(props: {
  label: string;
  valor: string;
  icone: string;
  cor: string;
  sub?: string;
}) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card adminuiux-card mb-3 mb-lg-4">
        <div className="card-body">
          <div className="row gx-3 align-items-center">
            <div className="col-auto">
              <span
                className={`avatar avatar-40 rounded-circle bg-opacity-10 ${props.cor}`}
              >
                <i className={`bi ${props.icone}`}></i>
              </span>
            </div>
            <div className="col">
              <p className="text-secondary small mb-1">{props.label}</p>
              <h5 className="mb-0">{props.valor}</h5>
              {props.sub && (
                <p className="small text-secondary mb-0">{props.sub}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustosPedidoListaPage() {
  const navigate = useNavigate();
  const { loja: slug } = useParams<{ loja: string }>();
  const codigo = codigoDaSlug(slug);

  const [loja, setLoja] = useState<Loja | null>(null);
  const [lista, setLista] = useState<PedidoResumo[]>([]);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [fBusca, setFBusca] = useState('');
  const [fSituacao, setFSituacao] = useState(TODOS);
  const [fMarca, setFMarca] = useState(TODOS);
  const [fMudanca, setFMudanca] = useState(false);
  const [numeroTiny, setNumeroTiny] = useState('');
  const [trazendo, setTrazendo] = useState(false);
  const [mudancasDe, setMudancasDe] = useState<PedidoResumo | null>(null);
  const [mudancas, setMudancas] = useState<Mudanca[]>([]);
  const { pode } = useAuth();
  const podeMarcar = pode('custos-pedido.pedido.editar');

  // Resolve a loja pelo código do slug
  useEffect(() => {
    api
      .get<Loja[]>('/lojas')
      .then((ls) => setLoja(ls.find((l) => l.codigo === codigo) ?? null))
      .catch(() => setLoja(null));
  }, [codigo]);

  async function carregar(lojaId: number) {
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams({ loja_id: String(lojaId) });
      if (fBusca.trim()) params.set('busca', fBusca.trim());
      if (fSituacao !== TODOS) params.set('situacao', fSituacao);
      if (fMarca !== TODOS) params.set('acompanhamento', fMarca);
      if (fMudanca) params.set('com_mudanca', 'true');
      const [r, l] = await Promise.all([
        api.get<Resumo>(`/custos-pedido/resumo?loja_id=${lojaId}`),
        api.get<PedidoResumo[]>(`/custos-pedido?${params.toString()}`),
      ]);
      setResumo(r);
      setLista(l);
    } catch (err) {
      setErro(
        err instanceof ApiError ? err.message : 'Não foi possível carregar.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (loja) carregar(loja.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loja, fSituacao, fMarca, fMudanca]);

  /** Marca na própria linha — sem abrir o pedido (dele: "bater o olho e decidir"). */
  async function marcar(p: PedidoResumo, valor: Marca) {
    try {
      const r = await api.post<PedidoResumo>(`/custos-pedido/${p.id}/acompanhamento`, { estado: valor });
      setLista((ls) => ls.map((x) => (x.id === p.id ? { ...x, acompanhamento: r.acompanhamento } : x)));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível marcar.');
    }
  }

  async function abrirMudancas(p: PedidoResumo) {
    setMudancasDe(p);
    setMudancas([]);
    try {
      setMudancas(await api.get<Mudanca[]>(`/custos-pedido/${p.id}/mudancas`));
    } catch {
      /* a lista vazia já diz que não veio */
    }
  }

  async function conferirMudancas() {
    if (!mudancasDe) return;
    try {
      setMudancas(await api.post<Mudanca[]>(`/custos-pedido/${mudancasDe.id}/mudancas/conferencia`));
      setLista((ls) => ls.map((x) => (x.id === mudancasDe.id ? { ...x, mudancas_abertas: 0 } : x)));
      toast.success('Conferido.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível conferir.');
    }
  }

  /** O pedido feito hoje não espera a madrugada: busca aqui ou no Tiny. */
  async function trazerDoTiny() {
    if (!loja || !numeroTiny.trim()) return;
    setTrazendo(true);
    try {
      const r = await api.get<PedidoResumo>(
        `/custos-pedido/tiny/buscar?loja_id=${loja.id}&numero=${encodeURIComponent(numeroTiny.trim())}`,
      );
      toast.success(`Pedido #${r.nr_pedido} — ${r.cliente ?? ''}`);
      setNumeroTiny('');
      setFBusca(r.nr_pedido);
      setFMarca(TODOS);
      await carregar(loja.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível trazer do Tiny.');
    } finally {
      setTrazendo(false);
    }
  }


  return (
    /* A LOJA SAIU DO TÍTULO E FOI PARA A DESCRIÇÃO. Ela estava num `<span>`
       cinza dentro do `<h5>` — funcionava, mas fazia o título mudar de tamanho
       conforme o nome da loja e quebrar em duas linhas no celular. Na descrição
       informa o mesmo e não disputa espaço com o nome da tela.

       ⚠️ Comentário entre PROPS não existe em JSX, nas duas formas: nem a de
       bloco nem a envolvida em chaves. Ele vai ANTES da tag. E cuidado ao
       escrever sobre isso num comentário: um fecha-bloco escrito por extenso
       dentro do texto encerra o comentário ali mesmo, e o resto do arquivo vira
       código — foi o que aconteceu aqui na primeira tentativa. */
    <CustomMolduraPagina
      titulo="Custos de Pedido"
      descricao={`${loja?.nome ?? codigo} — margem por pedido de venda`}
      acoes={
          <button
            className="btn btn-theme"
            onClick={() => navigate(`/custos-pedido/novo/${(slug ?? 'bz')}`)}
            disabled={!loja}
          >
            <i className="bi bi-plus-lg me-1"></i> Novo pedido
          </button>
      }
    >

      {/* Cards de resumo */}
      <div className="row">
        <StatCard
          label="Receita"
          valor={fmt(resumo?.receita_total)}
          icone="bi-graph-up-arrow"
          cor="bg-success text-success"
        />
        <StatCard
          label="Custo total"
          valor={fmt(resumo?.custo_total)}
          icone="bi-cash-stack"
          cor="bg-danger text-danger"
        />
        <StatCard
          label="Margem média"
          valor={
            resumo?.margem_media_pct != null
              ? `${resumo.margem_media_pct.toFixed(0)}%`
              : '—'
          }
          icone="bi-percent"
          cor="bg-primary text-primary"
        />
        <StatCard
          label="Pedidos"
          valor={String(resumo?.qtd_pedidos ?? 0)}
          icone="bi-receipt"
          cor="bg-warning text-warning"
        />
      </div>

      <div className="card adminuiux-card">
        <div className="card-body">
          {/* Filtros */}
          <div className="row g-2 mb-3">
            <div className="col-12 col-md">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  className="form-control"
                  placeholder="Buscar por nº do pedido ou cliente…"
                  value={fBusca}
                  onChange={(e) => setFBusca(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && loja) carregar(loja.id);
                  }}
                />
              </div>
            </div>
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fMarca}
                onChange={(e) => setFMarca(e.target.value)}
                aria-label="Acompanhamento"
              >
                <option value={TODOS}>Todos os pedidos</option>
                <option value="acompanhar">Acompanhados</option>
                <option value="duvida">Em dúvida</option>
                <option value="nao_acompanhar">Não acompanhados</option>
              </select>
            </div>
            <div className="col-6 col-md-auto d-flex align-items-center">
              <div className="form-check form-switch mb-0">
                <input
                  id="f-mudanca"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={fMudanca}
                  onChange={(e) => setFMudanca(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="f-mudanca">
                  Só com mudança
                </label>
              </div>
            </div>
            <div className="col-12 col-md-auto">
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={{ maxWidth: 130 }}
                  inputMode="numeric"
                  placeholder="Nº no Tiny"
                  value={numeroTiny}
                  onChange={(e) => setNumeroTiny(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') trazerDoTiny();
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-theme"
                  onClick={trazerDoTiny}
                  disabled={trazendo || !numeroTiny.trim()}
                  title="Traz o pedido do Tiny agora, sem esperar a madrugada"
                >
                  {trazendo ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-cloud-download"></i>}
                </button>
              </div>
            </div>
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fSituacao}
                onChange={(e) => setFSituacao(e.target.value)}
              >
                <option value={TODOS}>Todas as situações</option>
                {SITUACOES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-sm align-middle table-hover mb-0 text-nowrap"
                style={{ fontSize: '0.85rem' }}
              >
                <thead>
                  <tr
                    className="text-secondary text-uppercase"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <th className="text-center" title="Acompanhar o custo deste pedido?">Acompanhar</th>
                    <th className="text-nowrap">Pedido</th>
                    <th className="text-nowrap">Data</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Tiny</th>
                    <th className="text-end text-nowrap">Receita</th>
                    <th className="text-end text-nowrap">Custo</th>
                    <th className="text-end text-nowrap">Margem</th>
                    <th className="text-center">Situação</th>
                    <th className="text-end"></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/custos-pedido/${p.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="btn-group btn-group-sm" role="group" aria-label="Acompanhar">
                          {MARCAS.map((m) => {
                            const ativo = (p.acompanhamento ?? null) === m.valor;
                            return (
                              <button
                                key={String(m.valor)}
                                type="button"
                                className={`btn px-2 py-0 ${ativo ? m.ativo : 'btn-outline-secondary'}`}
                                title={m.titulo}
                                disabled={!podeMarcar}
                                onClick={() => !ativo && marcar(p, m.valor)}
                              >
                                <i className={`bi ${m.icone}`}></i>
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="fw-semibold text-nowrap">#{p.nr_pedido}</td>
                      <td className="text-nowrap text-secondary">{p.data ? formatarData(p.data) : '—'}</td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: 180 }}
                        title={p.cliente ?? ''}
                      >
                        {p.cliente ?? <span className="text-secondary">—</span>}
                      </td>
                      {/* Só o primeiro nome: a coluna cabe, e na loja todo mundo
                          se conhece pelo primeiro nome (dele, 28/09). */}
                      <td className="text-secondary text-nowrap" title={p.vendedor ?? ''}>
                        {p.vendedor ? p.vendedor.trim().split(/\s+/)[0] : '—'}
                      </td>
                      <td className="text-nowrap small" onClick={(e) => e.stopPropagation()}>
                        {p.sumiu_tiny ? (
                          <span className="badge casaa-selo-perigo" title="Não existe mais no Tiny">
                            sumiu
                          </span>
                        ) : (
                          <span className="text-secondary">
                            {p.situacao_tiny ? SITUACAO_TINY[p.situacao_tiny] ?? p.situacao_tiny : '—'}
                          </span>
                        )}
                        {/* O sinal é só o ícone — o texto vai no tooltip (dele, 28/09:
                            "não precisa ocupar esse espaço todo"). */}
                        {p.mudancas_abertas > 0 && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-1 align-baseline"
                            title="Mudou no Tiny — clique para ver o que mudou"
                            aria-label="Mudou no Tiny"
                            onClick={() => abrirMudancas(p)}
                          >
                            {/* Só o ícone, sem fundo, no laranja de aviso do tema
                                (`estilos/_custos-pedido.scss`). A classe vai NO ÍCONE:
                                no botão, o `btn-link` pinta por cima de azul. */}
                            <i className="bi bi-exclamation-triangle-fill casaa-custos-pedido-sinal-mudou"></i>
                          </button>
                        )}
                      </td>

                      <td className="text-end text-nowrap">
                        {p.valor_venda != null ? (
                          fmt(p.valor_venda)
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                      <td className="text-end text-nowrap">{fmt(p.custo_total)}</td>
                      <td className={`text-end text-nowrap fw-semibold ${corMargem(p.margem_pct)}`}>
                        {/* Sem custo lançado, "100%" mentiria: a margem é
                            desconhecida, não total (28/09, com os pedidos do Tiny). */}
                        {p.qtd_custos === 0 ? (
                          <span className="text-secondary fw-normal" title="Nenhum custo lançado ainda">—</span>
                        ) : p.margem_pct != null ? (
                          `${p.margem_pct.toFixed(0)}%`
                        ) : (
                          <span className="text-secondary fw-normal" title="Falta o valor da venda">
                            <i className="bi bi-dash-circle"></i>
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={`badge ${badgeSituacao(p.situacao)}`}>
                          {rotuloSituacao(p.situacao)}
                        </span>
                      </td>
                      <td className="text-end text-secondary">
                        <i className="bi bi-chevron-right"></i>
                      </td>
                    </tr>
                  ))}
                  {lista.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-5 text-center text-secondary">
                        Nenhum pedido encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
                {lista.length > 0 && (
                  <tfoot>
                    <tr className="fw-semibold border-top">
                      <td colSpan={7} className="text-secondary small">
                        {lista.length} pedido(s)
                      </td>
                      {/* O total vem do BACKEND, somado em Decimal — não se soma
                          aqui. Havia duas verdades nesta tela: este rodapé em
                          float de JS e o card "Custo total" vindo da API, que
                          podiam discordar em um centavo sem ninguém saber qual
                          valia. Regra da doc 07: dinheiro não se soma em JS. */}
                      <td className="text-end text-nowrap">{fmt(resumo?.custo_total)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
      {/* O QUE MUDOU NO TINY (ficha #44): no começo o app só avisa — quem
          olha decide, e "Conferido" apaga o sinal (fica quem e quando). */}
      <Modal className="casaa-modal" show={!!mudancasDe} onHide={() => setMudancasDe(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            Pedido #{mudancasDe?.nr_pedido} mudou no Tiny
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mudancas.length === 0 ? (
            <p className="text-secondary mb-0">Carregando…</p>
          ) : (
            mudancas.map((m) => (
              <div key={m.id} className={`border rounded p-2 mb-2 ${m.conferencia ? 'opacity-50' : ''}`}>
                <div className="small text-secondary mb-1">
                  {formatarData(m.deteccao)} ·{' '}
                  {m.tipo === 'sumiu' ? 'sumiu do Tiny' : m.tipo === 'voltou' ? 'voltou ao Tiny' : 'alterado'}
                  {m.conferencia && ` · conferido por ${m.conferido_por ?? '—'}`}
                </div>
                <div className="small" style={{ whiteSpace: 'pre-line' }}>
                  {m.resumo}
                </div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setMudancasDe(null)}>
            Fechar
          </button>
          {podeMarcar && mudancas.some((m) => !m.conferencia) && (
            <button type="button" className="btn btn-theme" onClick={conferirMudancas}>
              <i className="bi bi-check2 me-1"></i>Conferido
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
